import { createContext, PropsWithChildren, useContext, useEffect, useLayoutEffect, useRef } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';

import { DeferInviteSession } from './deferInviteSession';
import { inviteFlushDebug } from './inviteFlushDebug';
import useDeferInviteNotifications from './useDeferInviteNotifications';

type DeferInviteSessionContextType = {
  session: DeferInviteSession;
};

const DeferInviteSessionContext = createContext<DeferInviteSessionContextType | null>(null);

export const useDeferInviteSession = (): DeferInviteSession => {
  const ctx = useContext(DeferInviteSessionContext);
  if (!ctx) throw new Error('useDeferInviteSession must be used within DeferInviteSessionProvider');
  return ctx.session;
};

const DeferInviteSessionProvider = ({ children }: PropsWithChildren) => {
  const { matrixClient } = useMatrixContext();
  const isDeferred = useDeferInviteNotifications();
  const userId = matrixClient.getUserId();

  const sessionRef = useRef(new DeferInviteSession());
  const wasDeferredRef = useRef(false);
  const hasHydratedRef = useRef(false);

  if (userId && !hasHydratedRef.current) {
    sessionRef.current.hydrateFromSessionStorage(userId);
    hasHydratedRef.current = true;
  }

  useLayoutEffect(() => {
    const session = sessionRef.current;

    if (isDeferred && !wasDeferredRef.current) {
      session.beginDeferral(matrixClient);
      inviteFlushDebug('DEFER-SESSION', {
        event: 'beginDeferral',
        baselineRoomCount: matrixClient.getRooms().length,
        deferStartedAt: session.deferStartedAt,
      });
      if (userId) session.persistToSessionStorage(userId);
    }

    if (!isDeferred && wasDeferredRef.current) {
      const ended = session.endDeferral();
      inviteFlushDebug('DEFER-SESSION', {
        event: 'endDeferral',
        snapshotCount: ended.snapshots.length,
        deferStartedAt: ended.deferStartedAt,
      });
      if (userId) {
        try {
          sessionStorage.removeItem(`meedio:defer-invite:${userId}:state`);
        } catch {
          // storage unavailable
        }
      }
    }

    wasDeferredRef.current = isDeferred;
  }, [isDeferred, matrixClient, userId]);

  useEffect(() => {
    if (!userId) {
      const prevUserId = matrixClient.getUserId();
      if (prevUserId) sessionRef.current.clearOnLogout(prevUserId);
      hasHydratedRef.current = false;
    }
  }, [userId, matrixClient]);

  useEffect(() => {
    if (!userId || !isDeferred) return;

    const interval = setInterval(() => {
      sessionRef.current.persistToSessionStorage(userId);
    }, 5000);

    return () => clearInterval(interval);
  }, [userId, isDeferred]);

  return (
    <DeferInviteSessionContext.Provider value={{ session: sessionRef.current }}>
      {children}
    </DeferInviteSessionContext.Provider>
  );
};

export default DeferInviteSessionProvider;
